import { Fragment, type ReactNode, useState } from "react";
import type {
  PositionRequest,
  PositionResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { DisclosureToggle, useDisclosure } from "@/components/ui/Disclosure";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { useDraftEditor } from "@/hooks/use-draft-editor";
import { type MappableElement, mapped } from "./element-mapping";
import { ElementChip } from "./ElementPicker";
import { PositionForm } from "./PositionForm";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Note, NotSavedNote } from "@/components/ui/Note";
import { refusalDetail } from "@/services/api-client";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { positionRowId } from "../anchors";
import { arriveAt, useTermArrival } from "../../terms/term-route";

/** What a refusal says when the server never answered. */
const UNREACHED = "The amendment did not reach the server.";

/**
 * What a row may do to its position. Each is a server write; the set comes back
 * whole. A position is confirmed inside the analysis, never here.
 */
export interface PositionActions {
  /** Resolves once the server has taken the amendment; rejects when it refuses. */
  onAmend: (positionId: string, body: PositionRequest) => Promise<unknown>;
  onRemove: (position: PositionResponse) => void;
}

/** Why the tool proposed a suggested position, in its words to the attorney; nothing on the attorney's own. */
function Rationale({ position }: { position: PositionResponse }) {
  if (position.origin !== "suggested" || position.rationale === null) {
    return null;
  }
  return (
    <div data-testid="position-rationale">
      <p className="mb-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
        Why this was suggested
      </p>
      <p className="text-[12px] leading-relaxed">{position.rationale}</p>
    </div>
  );
}

/** Amend and remove. */
function RowActions({
  position,
  actions,
  busy,
  onEdit,
}: {
  position: PositionResponse;
  actions: PositionActions;
  busy: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="dense"
        variant="secondary"
        disabled={busy}
        onClick={onEdit}
        className="cursor-pointer"
      >
        Amend
      </Button>
      <Button
        type="button"
        size="dense"
        variant="danger-outline"
        disabled={busy}
        onClick={() => actions.onRemove(position)}
      >
        Remove
      </Button>
    </div>
  );
}

/** The mapped elements, the notes, and the two actions, shown when the row is open. */
function Body({
  position,
  elements,
  actions,
  busy,
  onEdit,
}: {
  position: PositionResponse;
  elements: readonly MappableElement[];
  actions: PositionActions;
  busy: boolean;
  onEdit: () => void;
}) {
  const { draft: notes, setDraft: setNotes } = useDraftEditor(
    position.notes ?? "",
  );
  const notesId = `position-${position.position_id}-notes`;
  const chips = mapped(position.element_ids, elements);
  const [refused, setRefused] = useState(false);
  useUnsavedDraft(refused);
  const saveNotes = () => {
    const written = notes.trim() === "" ? null : notes.trim();
    if (written === position.notes) return;
    setRefused(false);
    // The field keeps its words either way; a refusal says so beneath them.
    actions
      .onAmend(position.position_id, {
        title: position.title,
        element_ids: position.element_ids,
        notes: written,
      })
      .catch(() => {
        setRefused(true);
      });
  };
  return (
    <div data-testid="position-body" className="mt-3 flex flex-col gap-3">
      <div>
        <FieldLabel>Rests on invention elements</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((element) => (
            <ElementChip key={element.element_id} element={element} />
          ))}
          {chips.length === 0 && (
            <span className="text-[11.5px] text-muted-foreground">
              No elements mapped.
            </span>
          )}
        </div>
      </div>
      <Rationale position={position} />
      <div>
        <FieldLabel htmlFor={notesId} optional>
          Additional notes
        </FieldLabel>
        <Textarea
          id={notesId}
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          onBlur={saveNotes}
          placeholder="Anything you want recorded against this position"
          className="bg-card text-[12px]"
        />
        <NotSavedNote failed={refused} onRetry={saveNotes} />
      </div>
      <AmendmentHistory position={position} />
      <RowActions
        position={position}
        actions={actions}
        busy={busy}
        onEdit={onEdit}
      />
    </div>
  );
}

/**
 * How the position's argument changed: the wording it started from, then
 * each amendment's wording and why. Each amendment holds the wording it
 * replaced, so amendment N's wording is the next one's, or the current one's.
 */
export function AmendmentHistory({ position }: { position: PositionResponse }) {
  const amendments = position.amendments;
  if (amendments.length === 0) return null;
  const wordingAfter = (at: number) =>
    at + 1 < amendments.length ? amendments[at + 1].title : position.title;
  return (
    <div data-testid="amendment-history">
      <FieldLabel>Amendment history</FieldLabel>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[12px]">
        <dt className="text-muted-foreground">Original wording</dt>
        <dd>{amendments[0].title}</dd>
        {amendments.map((amendment, at) => (
          <Fragment key={amendment.amended_at}>
            <dt className="text-muted-foreground">Amendment {at + 1}</dt>
            <dd>{wordingAfter(at)}</dd>
            <dt className="text-muted-foreground">Amendment {at + 1} notes</dt>
            <dd>{amendment.note}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

/** The name, and beside it (never inside it) where the position came from. */
function Title({ position }: { position: PositionResponse }) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span
        data-testid="position-title"
        className="min-w-0 truncate text-[13.5px] font-bold"
      >
        {position.title}
      </span>
      {position.origin === "attorney" && (
        <span className="flex-none rounded border border-primary/20 bg-primary/[0.06] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary">
          Added by you
        </span>
      )}
    </span>
  );
}

/**
 * Amending in place: the form a new position is drafted in, saved only on an
 * explicit act. It closes once the server has taken the change; a refusal
 * keeps the words and says why under the form.
 */
export function AmendInPlace({
  position,
  elements,
  onAmend,
  busy,
  onDone,
}: {
  position: PositionResponse;
  elements: readonly MappableElement[];
  onAmend: PositionActions["onAmend"];
  busy: boolean;
  onDone: () => void;
}) {
  const [refusal, setRefusal] = useState<string | null>(null);
  return (
    <>
      <PositionForm
        idPrefix={`amend-${position.position_id}`}
        initial={{
          title: position.title,
          element_ids: position.element_ids,
          notes: position.notes ?? "",
        }}
        elements={elements}
        submitLabel="Save"
        amending
        busy={busy}
        onSubmit={(body) => {
          setRefusal(null);
          onAmend(position.position_id, body)
            .then(onDone)
            .catch((failure: unknown) =>
              setRefusal(refusalDetail(failure, UNREACHED)),
            );
        }}
        onCancel={onDone}
      />
      {refusal !== null && (
        <Note tone="destructive" role="alert" className="mt-1">
          {refusal}
        </Note>
      )}
    </>
  );
}

/**
 * One position: its number, its name, where it came from, and, expanded,
 * what it rests on and what may be done to it. Amending opens the same form a new position is drafted in and
 * saves only on an explicit act.
 */
export function PositionRow({
  position,
  elements,
  actions,
  busy,
  suggestion = null,
}: {
  position: PositionResponse;
  elements: readonly MappableElement[];
  actions: PositionActions;
  busy: boolean;
  /** A change Praxis suggests to this position: the row is outlined, needs attention and shows it, open or closed. */
  suggestion?: ReactNode;
}) {
  const { open, setOpen, trigger, panel } = useDisclosure();
  const [editing, setEditing] = useState(false);
  // Sent here by the Terminology panel: the row opens, on its form when its Edit sent it.
  useTermArrival(true, ({ recordId, edit }) => {
    if (recordId !== position.position_id) return;
    setOpen(true);
    setEditing(edit);
    requestAnimationFrame(() => {
      arriveAt(positionRowId(position.position_id));
    });
  });
  return (
    <Panel
      as="li"
      shape="row"
      id={positionRowId(position.position_id)}
      // Focusable by script alone: the run footer lands here when it points at
      // this position, and a list item takes focus only when told it may.
      tabIndex={-1}
      data-testid={`position-row-${position.position_id}`}
      className={cn("mb-2.5", suggestion !== null && "ring-2 ring-primary/60")}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex-none text-[11px] font-semibold text-muted-foreground">
          Position {position.number}
        </span>
        <Title position={position} />
        {suggestion !== null && (
          <StatusPill tone="destructive" size="sm" dot>
            Requires attention
          </StatusPill>
        )}
        <DisclosureToggle
          trigger={trigger}
          open={open}
          names={`position ${position.number}`}
          // A collapsed row reopens on its read view.
          onToggle={() => {
            setEditing(false);
          }}
        />
      </div>
      {suggestion}
      <div {...panel}>
        {open &&
          (editing ? (
            <AmendInPlace
              position={position}
              elements={elements}
              onAmend={actions.onAmend}
              busy={busy}
              onDone={() => setEditing(false)}
            />
          ) : (
            <Body
              position={position}
              elements={elements}
              actions={actions}
              busy={busy}
              onEdit={() => setEditing(true)}
            />
          ))}
      </div>
    </Panel>
  );
}
